<template>
  <el-card v-loading="loading">
    <el-header slot="header">
      <el-page-header @back="$router.back()" :content="`${user.name || user.email}'s Access`" />
    </el-header>

    <el-main>
      <section>
        <el-row>
          <el-col :span="24">
            <h4>Roles</h4>
            <el-divider />

            <el-checkbox-group v-model="user_roles" :disabled="isSaving">
              <div class="role" v-for="role in roles" :key="role.id">
                <el-checkbox :label="role.name">{{ role.display_name || role.name }}</el-checkbox>
              </div>
            </el-checkbox-group>
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="24">
            <h5>Permission</h5>
            <el-divider />

            <ul class="permissions">
              <li 
                v-for="permission in userPermissions" 
                :key="permission.id"
                class="permissions__item">
                <el-tag type="info">{{ permission.display_name || permission.name }}</el-tag>
              </li>
            </ul>
          </el-col>
        </el-row>
      </section>
    </el-main>

    <el-footer>
      <el-button type="primary" :loading="isSaving" @click="save">Save</el-button>
    </el-footer>
  </el-card>
</template>

<script>
  import axios, { RepositoryError } from '../../repositories';
  import { notify } from '../../utils';

  export default {
    name: 'ManageUser',
    async mounted() {
      this.loading = true;
      await Promise.all([
        this.fetch(),
        this.fetchRoles(),
        this.fetchPermissions(),
      ]);
      this.loading = false;
    },
    props: {
      user_id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        user: {
          name: '',
          email: '',
        },
        user_roles: [],
        user_permissions: [],
        roles: [],
        permissions: [],
        loading: false,
        isSaving: false,
      };
    },
    computed: {
      userPermissions() {
        return this.permissions.filter(
          permission => this.user_permissions.includes(permission.name)
        );
      },
    },
    methods: {
      async fetch() {
        try {
          const response = await axios.get(`/users/${this.user_id}`);

          this.user = response.data;
          this.user_roles = response.data.roles.map(role => role.name);
          this.user_permissions = response.data.permissions;
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
      },
      async fetchRoles() {
        try {
          const response = await axios.get('/auth/roles');

          this.roles = response.data;
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
      },
      async fetchPermissions() {
        try {
          const response = await axios.get('/auth/permissions');

          this.permissions = response.data;
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
      },
      async save() {
        try {
          this.isSaving = true;

          const response = await axios.put(`/auth/user-roles/${this.user_id}`, {
            roles: this.user_roles,
          });

          const permissions = response.data
            .map(role => role.permissions)
            .flat()
            .map(permission => permission.name);

          this.user_permissions = [...new Set(permissions)];
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        } finally {
          this.isSaving = false;
        }
      },
    },
  }
</script>

<style scoped>
  .role {
    margin-bottom: 2rem;
  }

  .permissions {
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
  }

  .permissions__item {
    margin: 5px;
  }
</style>
